import React, { useEffect, useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../Dialog'
import type { DialogProps } from '../Dialog'
import Button from '../Button'
import { dvhWithFallback } from '../../styles'
import { useTranslation } from '../../providers/TranslationProvider'
import { ConfirmationDialog, unsavedChangesConfirmation } from '../../dialogs'
import FileViewer from './FileViewer'
import type { FileViewerProps } from './FileViewer'

export interface FileViewerDialogProps extends Omit<DialogProps, 'onClose'>, FileViewerProps {
  title?: React.ReactNode
  onClose: () => void
  /**
   * Called when the user clicks Done in the footer. Omit (or pass viewOnly)
   * to render the dialog without a footer — useful for a read-only customer
   * lightbox where there's nothing to confirm.
   */
  onDone?: () => void
  /** Disable the Done button (e.g. while uploads are in progress). */
  doneDisabled?: boolean
  /** Show a spinner inside the Done button. */
  doneLoading?: boolean
  /**
   * When supplied, the footer's left button reads "Back" and calls this instead
   * of "Cancel". Used when the editor is opened from the Media Viewer so the user
   * can step back to the source picker. The corner ✕ still calls `onClose`.
   */
  onBack?: () => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  paper: {
    // Sized like the Line Item / Area edit dialogs (ENG-765 follow-up): a
    // centered, non-fullscreen `maxWidth="lg"` + `fullWidth` dialog on iPad,
    // fullscreen only on real phones (down('xs')). Width is left to MUI's
    // fullWidth + maxWidth="lg" and the core Dialog's `minWidth: 728`.
    //
    // dvh tracks the *visible* viewport; `vh` resolves against the larger frame
    // (browser toolbars hidden) on iPad Safari/Chrome, so a `vh`-sized dialog
    // extends below the visible fold — the page then feels scrollable with a
    // grey bar at the bottom (#21357, same class as #21015). `dvhWithFallback`
    // keeps `vh` as the fallback so the dialog never collapses on browsers
    // without dvh (Safari <15.4) and upgrades to dvh via @supports where
    // available.
    ...dvhWithFallback('height', 'calc(100dvh - 96px)', 'calc(100vh - 96px)'),
    // Fill the visible viewport on iPad but cap the height so the dialog doesn't
    // balloon to near-full-height on large desktop monitors (unlike the
    // content-driven Line Item/Area dialogs, this one forces a `height`). The
    // dvh `height` rule above already keeps it within the visible viewport, so
    // this is purely an upper ceiling for big screens.
    maxHeight: 900,
    // Key off the *actual* fullscreen state (the down('xs') auto fullscreen on
    // phones) rather than a width breakpoint, and override the core Dialog's
    // `maxHeight: 100vh !important` on paperFullScreen. Since 100dvh <= 100vh
    // the leftover !important maxHeight no longer clamps the height below the
    // visible viewport.
    '&$paperFullScreen': {
      margin: 0,
      ...dvhWithFallback('height', '100dvh', '100vh'),
      ...dvhWithFallback('maxHeight', '100dvh', '100vh')
    }
  },
  paperFullScreen: {},
  content: {
    padding: 0,
    height: '100%',
    // Let the flex child shrink below its intrinsic content height; without this
    // the inner FileViewer (preview + gallery strip) can push the paper past its
    // own height and MUI's paper (overflowY: auto) scrolls — the "feels
    // scrollable" symptom (#21357).
    minHeight: 0,
    overflow: 'hidden'
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  }
}))

function FileViewerDialog(props: FileViewerDialogProps) {
  const { __ } = useTranslation()
  const {
    title,
    onClose,
    onDone,
    files,
    initialKey,
    selectedKey,
    onSelectedKeyChange,
    onFileChange,
    onEditSave,
    onUpload,
    onPickFiles,
    onAddProvider,
    addProviderLabel,
    onReorder,
    onChangeFile,
    onChangeProviderFile,
    onSyncProviderFile,
    canSyncFile,
    syncingKey,
    onDelete,
    onDeleteMany,
    titleOnly,
    viewOnly,
    doneDisabled,
    doneLoading,
    onBack,
    ...dialogProps
  } = props
  const classes = useStyles({})

  // When the editor is open, hide the dialog's Cancel/Done footer — the editor
  // renders its own Cancel | Save Changes footer.
  const [editing, setEditing] = useState(false)
  // The open editor's unsaved-markup state, lifted up so the corner ✕ can guard
  // against discarding edits — the editor's own Back/Cancel buttons already do.
  const [editorDirty, setEditorDirty] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const [localConfirming, setLocalConfirming] = useState(false)

  // The corner ✕ closes the whole dialog, bypassing the editor's own Back/Cancel
  // guard — so prompt here when the editor has unsaved markup (ENG-851).
  const requestClose = () => {
    if (editing && editorDirty) setConfirmClose(true)
    else onClose()
  }

  const handleDone = () => {
    setLocalConfirming(true)
    onDone()
  }

  // Clear the local confirm latch if the parent *rejects* the save instead of
  // closing. On a successful confirm the parent dismisses this dialog (unmount),
  // so the latch is only left dangling on a blocked save — the over-limit
  // title/caption block re-renders with the viewer jumped to the offending file
  // (a changed `selectedKey`) without dismissing (#21295 / ENG-731). Resetting on
  // that jump keeps Done from staying stuck spinning after a blocked save
  // (ENG-867). A successful async save doesn't change `selectedKey`, so the
  // spinner still persists through it and guards against a double-submit.
  useEffect(() => {
    setLocalConfirming(false)
  }, [selectedKey])

  return (
    <Dialog
      {...dialogProps}
      maxWidth="lg"
      fullWidth
      onClose={requestClose}
      classes={{ paper: classes.paper, paperFullScreen: classes.paperFullScreen }}
    >
      <DialogTitle
        classes={{ root: classes.dialogTitle }}
        rightContent={
          <IconButton size="small" onClick={requestClose} aria-label={__('Close')}>
            <CloseIcon />
          </IconButton>
        }
      >
        {title ?? __('Edit Media')}
      </DialogTitle>
      <DialogContent classes={{ root: classes.content }}>
        <FileViewer
          files={files}
          initialKey={initialKey}
          selectedKey={selectedKey}
          onSelectedKeyChange={onSelectedKeyChange}
          onFileChange={onFileChange}
          onEditSave={onEditSave}
          onUpload={onUpload}
          onPickFiles={onPickFiles}
          onAddProvider={onAddProvider}
          addProviderLabel={addProviderLabel}
          onReorder={onReorder}
          onChangeFile={onChangeFile}
          onChangeProviderFile={onChangeProviderFile}
          onSyncProviderFile={onSyncProviderFile}
          canSyncFile={canSyncFile}
          syncingKey={syncingKey}
          onDelete={onDelete}
          onDeleteMany={onDeleteMany}
          titleOnly={titleOnly}
          viewOnly={viewOnly}
          onEditingChange={setEditing}
          onEditorDirtyChange={setEditorDirty}
        />
      </DialogContent>
      {!editing && !viewOnly && onDone && (
        <DialogActions
          leftButton={
            <Button onClick={onBack ?? onClose} variant="textNoPadding">
              {onBack ? __('Back') : __('Cancel')}
            </Button>
          }
        >
          <Button
            variant="contained"
            onClick={handleDone}
            disabled={doneDisabled}
            loading={doneLoading || localConfirming}
          >
            {__('Done')}
          </Button>
        </DialogActions>
      )}

      <ConfirmationDialog
        open={confirmClose}
        {...unsavedChangesConfirmation}
        onConfirm={onClose}
        onCancel={() => setConfirmClose(false)}
      />
    </Dialog>
  )
}

export default FileViewerDialog
